import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Brainstorming from '../components/Brainstorming';
import { BrainCircuit, Share2, ArrowLeft, Sparkles, Lock, Shield, User, ArrowRight, Loader2 } from 'lucide-react';
import { cn, formatTime, getDurationInSeconds } from '../utils';
import { getTheme } from '../utils/themes';
import { useRoom } from '../hooks/useRoom';
import { SESSION_ROLES } from '../utils/permissions';

const BrainstormRoom = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    roomData,
    sessionState,
    sessionRole,
    setSessionRole,
    timeLeft,
    isUnlocked,
    showPasswordPrompt,
    accessError,
    isInitialLoading,
    isModerator,
    handlePasswordSubmit,
    handleInvite
  } = useRoom(id);

  const [passwordInput, setPasswordInput] = useState('');
  const theme = getTheme(roomData?.settings?.room_type || 'Brainstorm');

  const onPasswordSubmit = (e) => {
    e.preventDefault();
    handlePasswordSubmit(passwordInput);
  };

  if (isInitialLoading || !roomData) {
    return (
      <div className="min-h-screen bg-brand-light flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={32} className={cn("animate-spin mx-auto mb-4", theme.text)} />
          <p className="text-gray-500 font-medium mb-4">Loading brainstorm room...</p>
          <button onClick={() => navigate(-1)} className={cn("font-bold text-sm", theme.text)}>Go Back</button>
        </div>
      </div>
    );
  }

  const expectedDurationSeconds = getDurationInSeconds(roomData?.settings?.duration);

  return (
    <div className="min-h-screen bg-brand-light relative overflow-x-hidden">
      <div className="fixed inset-0 pointer-events-none opacity-40">
        <div className={cn("absolute top-[-10%] left-[-10%] w-[40%] h-[40%] blur-[120px] rounded-full", theme.lightBg)} />
        <div className={cn("absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] blur-[120px] rounded-full", theme.lightBg)} />
      </div>

      <AnimatePresence>
        {showPasswordPrompt && !isUnlocked && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-brand-light flex items-center justify-center px-4"
          >
            <div className={cn("max-w-md w-full bg-white rounded-[2.5rem] border border-gray-100 p-8 shadow-2xl text-center", theme.shadow)}>
              <div className={cn("w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6", theme.lightBg, theme.text)}>
                <Lock size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Private Board</h2>
              <p className="text-gray-500 font-medium mb-8">This ideation board is password protected. Please enter the access code to join.</p>
              
              <form onSubmit={onPasswordSubmit} className="space-y-4">
                <div>
                  <input 
                    type="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder="Enter password"
                    className={cn(
                      "w-full px-6 py-4 bg-gray-50 border-2 rounded-2xl font-bold transition-all text-center text-xl tracking-widest outline-none",
                      theme.ring,
                      accessError ? "border-red-100 bg-red-50 text-red-600 focus:ring-red-100" : cn("border-gray-50", `focus:${theme.border}`)
                    )}
                    autoFocus
                  />
                  {accessError && (
                    <p className="text-red-500 text-xs font-bold mt-3 animate-in fade-in slide-in-from-top-1">{accessError}</p>
                  )}
                </div>
                <button 
                  type="submit"
                  className={cn("w-full text-white font-bold py-4 rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2", theme.bg, theme.shadow, theme.hover)}
                >
                  Access Board
                  <ArrowRight size={20} />
                </button>
                <button 
                  type="button"
                  onClick={() => navigate(-1)}
                  className="w-full text-gray-400 font-bold py-2 text-sm hover:text-gray-600 transition-colors"
                >
                  Go Back
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={cn("bg-white/80 backdrop-blur-md border-b sticky top-0 z-30", theme.lightBorder)}>
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <button onClick={() => navigate(-1)} className={cn("p-2 rounded-xl transition-colors shrink-0", theme.text, theme.hoverBg)}>
              <ArrowLeft size={20} />
            </button>
            <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-lg shrink-0", theme.bg, theme.shadow)}>
               <BrainCircuit size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-sm md:text-lg font-bold text-gray-900 leading-tight truncate">{roomData.title}</h1>
              <div className="flex items-center gap-2 overflow-hidden">
                 <span className={cn("flex items-center gap-1 text-[9px] md:text-xxs font-bold shrink-0", theme.text)}>
                    <Sparkles size={10} />
                    Ideation Active
                 </span>
                 <span className="w-1 h-1 bg-gray-300 rounded-full shrink-0"></span>
                 <div className="flex items-center gap-1 truncate">
                   <Shield size={10} className={cn("shrink-0", theme.text)} />
                   <span className="text-[9px] md:text-xxs font-bold text-gray-900 truncate">{roomData.host || 'Host'}</span>
                 </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
             <div className="flex flex-col gap-1 text-right">
                <div className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-xl border font-bold text-xxs uppercase",
                  sessionState === 'upcoming' ? "bg-amber-50 text-amber-600 border-amber-100" : 
                  (expectedDurationSeconds > 0 && timeLeft > expectedDurationSeconds) ? "bg-orange-50 text-orange-600 border-orange-100" : cn(theme.lightBg, theme.text, theme.lightBorder)
                )}>
                  <div className={cn(
                    "w-1.5 h-1.5 rounded-full animate-pulse",
                    sessionState === 'upcoming' ? "bg-amber-400" : (expectedDurationSeconds > 0 && timeLeft > expectedDurationSeconds) ? "bg-orange-500" : theme.bg
                  )} />
                  {formatTime(timeLeft)}
                </div>
             </div>
             <button 
                onClick={handleInvite}
                className={cn("flex items-center gap-2 text-white px-4 py-2 rounded-xl font-bold text-xs shadow-lg transition-all", theme.bg, theme.shadow, theme.hover)}
             >
                <Share2 size={14} />
                <span className="hidden sm:inline">Share Board</span>
             </button>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-8">
        <div className={cn("max-w-[1600px] mx-auto bg-white rounded-[2.5rem] border shadow-2xl overflow-hidden flex flex-col h-[calc(100vh-200px)] md:h-[calc(100vh-240px)] min-h-[500px]", theme.lightBorder, theme.shadowLight)}>
           <div className="flex-1 min-h-0">
              <Brainstorming 
                sessionState={sessionState} 
                isModerator={isModerator} 
                sessionId={roomData?.id}
                communityId={roomData?.community_id}
                roomData={roomData}
              />
           </div>
        </div>
      </div>

      {isModerator && (
        <div className="fixed bottom-32 md:bottom-8 left-1/2 -translate-x-1/2 bg-white/80 backdrop-blur-xl border p-2 rounded-2xl shadow-2xl flex gap-2 z-40 border-gray-100">
            <button 
              onClick={() => setSessionRole(SESSION_ROLES.HOST)}
              className={cn("p-3 rounded-xl transition-all", sessionRole === SESSION_ROLES.HOST ? cn("text-white shadow-lg", theme.bg, theme.shadow) : "text-gray-400 hover:bg-gray-50")}
              title="Host View"
            >
              <Shield size={20} />
            </button>
            <button 
              onClick={() => setSessionRole(SESSION_ROLES.PARTICIPANT)}
              className={cn("p-3 rounded-xl transition-all", sessionRole === SESSION_ROLES.PARTICIPANT ? cn("text-white shadow-lg", theme.bg, theme.shadow) : "text-gray-400 hover:bg-gray-50")}
              title="Participant View"
            >
              <User size={20} />
            </button>
        </div>
      )}
    </div>
  );
};

export default BrainstormRoom;
